import { SiteUrls } from '@tableau/shared-urls';
import { TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import { IPromise } from 'angular';
import * as React from 'react';
import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { LockStatusHelper, LockUnlockPermissionsState, NestedProjectsPermissionsState } from '../../ts/helpers/LockStatusHelper';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { L10n } from '../../ts/L10n/L10n';
import { ILongRunningAction } from '../../ts/models/ILongRunningAction';
import { IProjectDetailInfo } from '../../ts/models/IProjectInfo';
import { Set } from '../../ts/models/Set';
import { Databases } from '../../ts/resources/Databases';
import { Projects } from '../../ts/resources/Projects';
import { ISetContentControlledPermissionsParams } from '../../ts/ServerApi/requests/SetContentControlledPermissionsRequest';
import { ISetProjectControlledPermissionsParams } from '../../ts/ServerApi/requests/SetProjectControlledPermissionsRequest';
import { IActionResult } from '../../ts/ServerApi/types/IActionResult';
import { IEmptyResult, IResult } from '../../ts/ServerApi/types/IResponse';
import { ITypedIdWithName } from '../../ts/ServerApi/types/ITypedId';
import { LongRunningActionService } from '../../ts/services/LongRunningActionService';
import { ErrorReason, ServerService } from '../../ts/services/ServerService';
import { IToastOptions, ToasterService, ToastType } from '../../ts/services/ToasterService';
import { AuthType, isAuthorizableContainer } from './PermissionsDialog';
import { PermissionsLockDialog } from './PermissionsLockDialog';
import { PermissionsDialogStyles } from './style/PermissionsDialogStyle';
import { PermissionsLockUnlockStyles } from './style/PermissionsLockUnlockStyle';

export const EDIT_LINK_TESTID = 'lock-unlock-project-permissions';
export const CONTROLLING_CONTENT_LINK_TESTID = 'controlling-content';

export interface ControllingContent {
  name: string;
  url: string;
}

export interface ContainerMessages {
  title: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string}) => string;
  changeSuccess: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string, CONTAINER_NAME: string, PERMISSION_SOURCE: string}) => string;
  changeSuccessLocked: (a: {CONTAINER_COUNT: number}) => string;
  notFound: (a: {CONTAINER_NAME: string}) => string;
  warning: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string, CONTAINER_NAME: string}) => string;
  warningIncludingNested?: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string, CONTAINER_NAME: string}) => string;
  lockedDescription: () => string;
  lockedIncludingNestedDescription?: () => string;
  lockedNestedDescription?: () => string;
  lockedRadioButtonLabel: () => string;
  mixedDescription: () => string;
  unlockedDescription: () => string;
  unlockedRadioButtonLabel: () => string;
}

export const DatabaseMessages: ContainerMessages = {
  title: Messages.lockContainerPermissions_dialogTitleDatabase,
  changeSuccess: Messages.lockContainerPermissions_lockPermissionsChangeSuccessDatabase,
  changeSuccessLocked: Messages.lockContainerPermissions_lockPermissionsChangeSuccessLockedDatabase,
  notFound: Messages.lockContainerPermissions_errorContainerNotFoundDatabase,
  warning: Messages.lockContainerPermissions_lockWarningDatabase,
  lockedDescription: Messages.lockPermissions_controllingDatabaseLocked,
  lockedRadioButtonLabel: Messages.lockContainerPermissions_lockDescriptionDatabase,
  mixedDescription: Messages.lockPermissions_controllingDatabaseMixed,
  unlockedDescription: Messages.lockPermissions_controllingDatabaseUnlocked,
  unlockedRadioButtonLabel: Messages.lockContainerPermissions_unlockDescriptionDatabase,
};

export const ProjectMessages: ContainerMessages = {
  title: Messages.lockContainerPermissions_dialogTitleProject,
  changeSuccess: Messages.lockContainerPermissions_lockPermissionsChangeSuccessProject,
  changeSuccessLocked: Messages.lockContainerPermissions_lockPermissionsChangeSuccessLockedProject,
  notFound: Messages.lockContainerPermissions_errorContainerNotFoundProject,
  warning: Messages.lockContainerPermissions_lockWarningProject,
  warningIncludingNested: Messages.lockContainerPermissions_lockWarningProjectAndNestedProjects,
  lockedDescription: Messages.lockPermissions_controllingProjectLocked,
  lockedIncludingNestedDescription: Messages.lockPermissions_controllingProjectLockedIncludingSubprojects,
  lockedNestedDescription: Messages.lockPermissions_nestedLockedProject,
  lockedRadioButtonLabel: Messages.lockContainerPermissions_lockDescriptionProject,
  mixedDescription: Messages.lockPermissions_controllingProjectMixed,
  unlockedDescription: Messages.lockPermissions_controllingProjectUnlocked,
  unlockedRadioButtonLabel: Messages.lockContainerPermissions_unlockDescriptionProject,
};

export type PermissionsLockUnlockProps = {
  /** The authorizables that this component is for */
  authorizables: Set<any>;

  /** Type of authorizable this is for */
  authorizableType: AuthType;

  /** Databases Resource that is used to make lock and unlock requests */
  databasesResource: Databases;

  /** LongRunningActionService resource */
  longRunningActionService: LongRunningActionService

  /** The string to render if the component is not used for a project */
  nonProjectMessage: string;

  /** Projects Resource */
  projectsResource: Projects;

  /** ServerService resource */
  serverService: ServerService;

  /** ID of an authorizable this component is for to the content that controls its permissions */
  sourceAuthorizablesById: _.Dictionary<ITypedIdWithName>;

  /** ToasterService resource */
  toasterService: ToasterService;

  /** Callback for when a change to lock status has successfully finished */
  updateLockStatus: (lockState: LockUnlockPermissionsState, nestedProjectsState: NestedProjectsPermissionsState) => void;
};

export type PermissionsLockUnlockState = {
  showDialog: boolean;
  busyUnlocking: boolean;
  percentComplete: number;
  controllingContent?: ControllingContent; // the content that controls all the authorizables this component is for
};

export class PermissionsLockUnlock extends React.Component<PermissionsLockUnlockProps, PermissionsLockUnlockState> {
  private containerMessages: ContainerMessages;
  private siteUrls: SiteUrls;

  constructor(props: PermissionsLockUnlockProps) {
    super(props);
    this.setContainerMessages();
    this.setSiteUrls();
    this.state = {
      showDialog: false,
      busyUnlocking: false,
      percentComplete: 0,
    };
  }

  public componentDidMount() {
    this.setControllingContent();
  }

  public componentDidUpdate(prevProps: PermissionsLockUnlockProps) {
    // setControllingContent needs to recalculate the controlling content when sourceAuthorizablesById has changed
    if (!_.isEqual(prevProps.sourceAuthorizablesById, this.props.sourceAuthorizablesById)) {
      this.setControllingContent();
    }
  }

  private setContainerMessages = (): void => {
    switch(this.props.authorizableType) {
      case AuthType.PROJECT:
        this.containerMessages = ProjectMessages;
        break;
      case AuthType.DATABASE:
        this.containerMessages = DatabaseMessages;
        break;
    }
  }

  private setSiteUrls = (): void => {
    this.siteUrls = new SiteUrls({
      siteUrlName: this.props.serverService.sessionSiteUrlName(),
      queryString: window.location.search,
    });
  }

  private setControllingContent = (): void => {
    // For now this method only handles the case where the controlling content is Project.
    // However, the controlling content could also be Database/Table (if authorizableType is Table) or Workbook/View (if authorizableType is View)
    // TODO: TFS 1029168 for handling other possible types of controlling content
    if (FeatureFlags.lockingSubProjectsEnabled && this.props.sourceAuthorizablesById[this.props.authorizables.first().id]) {
      const controllingContent: ITypedIdWithName = this.props.sourceAuthorizablesById[this.props.authorizables.first().id];
      if (controllingContent.id && controllingContent.type && controllingContent.type === AuthType.PROJECT) {
        // check if all authorizables are controlled by the same project
        const allControlledBySameProject: boolean = this.props.authorizables.all(authorizable => {
          const controllingContentIdToCompare: string = this.props.sourceAuthorizablesById[authorizable.id] && this.props.sourceAuthorizablesById[authorizable.id].id;
          return controllingContentIdToCompare === controllingContent.id;
        });
        if (allControlledBySameProject) {
          // check if current user can see the controlling project, if not then getById will not return IProjectDetailInfo
          this.props.projectsResource.getById(controllingContent.id).then((value: IProjectDetailInfo) => {
            this.setState({
              controllingContent: {
                name: value.name,
                url: this.siteUrls.projectUrl(value.id),
              },
            });
          });
        }
      }
    }
  }

  private popToast = (type: ToastType, message: string): void => {
    let toast: IToastOptions;
    switch (type) {
      case ToastType.Error:
        toast = {
          type,
          text: message,
        };
        break;
      case ToastType.Info:
        const count = this.props.authorizables.size();
        const text = this.containerMessages.changeSuccess({
          CONTAINER_COUNT: count,
          CONTAINER_COUNT_LOC: L10n.localizedCount(count),
          CONTAINER_NAME: L10n.elideIfLong(this.props.authorizables.first().name),
          PERMISSION_SOURCE: message,
        });
        toast = { type, text, };
        break;
    }
    this.props.toasterService.pop(toast);
  }

  private handleLockResult = (result: IEmptyResult, containerNotFoundError: CommonErrorCode): IEmptyResult => {
    if (result.errors && result.errors.length) {
      const errorMessage: string = result.errors[0].code === containerNotFoundError ?
        this.containerMessages.notFound({ CONTAINER_NAME: L10n.elideIfLong(this.props.authorizables.first().name), }) :
        Messages.errorUnknown();
      this.popToast(ToastType.Error, errorMessage);
    } else {
      this.popToast(ToastType.Info, this.containerMessages.changeSuccessLocked({ CONTAINER_COUNT: this.props.authorizables.size(), }));
    }
    return result;
  }

  private lockProjectPermissions = (nestedProjectsPermissionsIncluded?: boolean): IPromise<IEmptyResult> => {
    let setProjectControlledPermissionsParams: ISetProjectControlledPermissionsParams = {
      ids: this.props.authorizables.pluck('id'),
    };

    if (FeatureFlags.lockingSubProjectsEnabled) {
      setProjectControlledPermissionsParams.nestedProjectsPermissionsIncluded = nestedProjectsPermissionsIncluded;
    }

    return this.props.projectsResource.setProjectControlledPermissions(setProjectControlledPermissionsParams)
    .then((result: IEmptyResult) => { return this.handleLockResult(result, CommonErrorCode.ProjectNotFound); });
  }

  private lockDatabasePermissions = (): IPromise<IEmptyResult> => {
    const ids: string[] = this.props.authorizables.pluck('id');


    return this.props.databasesResource.enableDatabaseControlledPermissions(ids)
    .then((result: IEmptyResult) => { return this.handleLockResult(result, CommonErrorCode.DatabaseNotFound); });
  }

  private unlockProjectPermissions = (nestedProjectsPermissionsIncluded?: boolean): IPromise<any> => {
    const permissionsChangeSuccessCallback = (result: ILongRunningAction) => {
      if (result && !result.succeeded) {
        permissionsChangeErrorCallback(ErrorReason.Unknown);
      } else {
        this.popToast(ToastType.Info, Messages.lockProjectPermissionsChangeSuccess_unlocked({ PROJECT_COUNT: this.props.authorizables.size() }));
        this.setState({ busyUnlocking: false, percentComplete: result.percentComplete, showDialog: false, });
      }
      return result.response;
    };

    const permissionsChangeErrorCallback = (reason: ErrorReason) => {
      this.popToast(ToastType.Error, Messages.errorUnknown());
      this.setState({ busyUnlocking: false, showDialog: false, });
    };

    const progressNotifyCallback = (action: ILongRunningAction) => {
      this.setState({ busyUnlocking: true, percentComplete: action.percentComplete, });
    };

    let setContentControlledPermissionsParams: ISetContentControlledPermissionsParams = {
      ids: this.props.authorizables.pluck('id'),
    };

    if (FeatureFlags.lockingSubProjectsEnabled) {
      setContentControlledPermissionsParams.nestedProjectsPermissionsIncluded = nestedProjectsPermissionsIncluded;
    }
    return this.props.projectsResource.setContentControlledPermissions(setContentControlledPermissionsParams)
    .then((result: IActionResult) => {
      const progress = this.props.longRunningActionService.trackAsyncJobProgress(result.actionId);
      return progress.promise.then(permissionsChangeSuccessCallback, permissionsChangeErrorCallback, progressNotifyCallback);
    });
  }

  private unlockDatabasePermissions = (): IPromise<any> => {
    const permissionsChangeSuccessCallback = (result: ILongRunningAction) => {
      if (result && !result.succeeded) {
        permissionsChangeErrorCallback(ErrorReason.Unknown);
      } else {
        this.popToast(ToastType.Info, Messages.lockDatabasePermissionsChangeSuccess_unlocked({ DATABASE_COUNT: this.props.authorizables.size() }));
        this.setState({ busyUnlocking: false, percentComplete: result.percentComplete, showDialog: false, });
      }
      return result.response;
    };

    const permissionsChangeErrorCallback = (reason: ErrorReason) => {
      this.popToast(ToastType.Error, Messages.errorUnknown());
      this.setState({ busyUnlocking: false, showDialog: false, });
    };

    const progressNotifyCallback = (action: ILongRunningAction) => {
      this.setState({ busyUnlocking: true, percentComplete: action.percentComplete, });
    };

    const ids: string[] = this.props.authorizables.pluck('id');

    return this.props.databasesResource.disableDatabaseControlledPermissions(ids)
    .then((result: IActionResult) => {
      const progress = this.props.longRunningActionService.trackAsyncJobProgress(result.actionId);
      return progress.promise.then(permissionsChangeSuccessCallback, permissionsChangeErrorCallback, progressNotifyCallback);
    });
  }

  private requestUpdateLockStatus = (newLockState: LockUnlockPermissionsState, newNestedProjectsState?: NestedProjectsPermissionsState) => {
    switch(this.props.authorizableType) {
      case AuthType.PROJECT:
        const nestedProjectsPermissionsIncluded = newNestedProjectsState === NestedProjectsPermissionsState.Included;
        if (newLockState === LockUnlockPermissionsState.Locked) {
          return this.lockProjectPermissions(nestedProjectsPermissionsIncluded);
        } else {
          return this.unlockProjectPermissions(nestedProjectsPermissionsIncluded);
        }
      case AuthType.DATABASE:
      if (newLockState === LockUnlockPermissionsState.Locked) {
        return this.lockDatabasePermissions();
      } else {
        return this.unlockDatabasePermissions();
      }
    }
  }

  private handleButtonClick = (): void => {
    this.setState({ showDialog: true, });
  }

  private handleCloseDialog = (): void => {
    this.setState({ showDialog: false, });
  }

  private handleUpdateLockStatus = (newLockState: LockUnlockPermissionsState, newNestedProjectsState?: NestedProjectsPermissionsState): void => {
    this.requestUpdateLockStatus(newLockState, newNestedProjectsState).then((result: IResult) => {
      if (!ResultHelper.hasErrors(result)) {
        this.props.updateLockStatus(newLockState, newNestedProjectsState);
        this.handleCloseDialog();
      }
    });
  }

  private getLockState = (): LockUnlockPermissionsState => {
    return LockStatusHelper.getLockState(this.props.authorizables);
  }

  /** returns whether the the authorizables allow for locking permissions (they are containers they control their own permissions) */
  private canLockPermissions = (): boolean => {
    if (FeatureFlags.lockingSubProjectsEnabled) {
      // is a container and controls its own permissions
      return isAuthorizableContainer(this.props.authorizableType) && !this.props.authorizables.any(p => this.props.sourceAuthorizablesById[p.id] && p.id !== this.props.sourceAuthorizablesById[p.id].id);
    }
    // is a project and there are no children projects or is a database
    return this.props.authorizableType === AuthType.DATABASE || (this.props.authorizableType === AuthType.PROJECT && !this.props.authorizables.any(p => !p.topLevelProject));
  }

  private getLockMessage = (lockState: LockUnlockPermissionsState, nestedProjectsState: NestedProjectsPermissionsState): string => {
    if (isAuthorizableContainer(this.props.authorizableType)) {
      if (FeatureFlags.lockingSubProjectsEnabled) {
        switch (lockState) {
          case LockUnlockPermissionsState.Locked:
            if (this.props.authorizableType === AuthType.PROJECT) {
              switch (nestedProjectsState) {
                case NestedProjectsPermissionsState.Included:
                  // if it cannot lock permissions, it is a nested project in a locked tree and is controlled by a parent
                  return this.canLockPermissions() ?
                    this.containerMessages.lockedIncludingNestedDescription() :
                    this.containerMessages.lockedNestedDescription();
                case NestedProjectsPermissionsState.Excluded:
                  return this.containerMessages.lockedDescription();
                case NestedProjectsPermissionsState.Mixed:
                  return this.containerMessages.mixedDescription();
              }
            }
            return this.containerMessages.lockedDescription();
          case LockUnlockPermissionsState.Unlocked:
            return this.containerMessages.unlockedDescription();
          case LockUnlockPermissionsState.Mixed:
            return this.containerMessages.mixedDescription();
        }
      }
      return LockStatusHelper.getLockMessage(lockState);
    } else {
      return this.props.nonProjectMessage;
    }
  }

  private renderContent = (lockState: LockUnlockPermissionsState, nestedProjectsState?: NestedProjectsPermissionsState): JSX.Element => {
    return this.state.busyUnlocking ? this.renderBusyUnlocking() : this.renderNotBusy(lockState, nestedProjectsState);
  }

  private renderNotBusy = (lockState: LockUnlockPermissionsState, nestedProjectsState: NestedProjectsPermissionsState): JSX.Element => {
    // when locking subprojects feature flag is on, sourceAuthorizablesById is necessary to figure out the right lock message and link to show, 
    // so in that case render nothing until sourceAuthorizablesById is available
    return FeatureFlags.lockingSubProjectsEnabled && _.isEmpty(this.props.sourceAuthorizablesById) ? null : (
      <span data-tb-test-id="permissions-lock">
        <span data-tb-test-id="permissions-lock-label" className={ PermissionsLockUnlockStyles.Label }>
          { this.getLockMessage(lockState, nestedProjectsState) }
        </span>
        {
          this.canLockPermissions() ? this.renderEditLink() : this.state.controllingContent && this.renderControllingContentLink()
        }
      </span>
    );
  }

  private renderEditLink = (): JSX.Element => {
    return (
      <TextLinkWidget
        className={ PermissionsLockUnlockStyles.TextLink }
        kind={ TextLinkKind.Standard }
        onClick={ this.handleButtonClick }
        testId={ EDIT_LINK_TESTID }
      >
        { Messages.lockProjectPermissions_edit() }
      </TextLinkWidget>
    );
  }

  private renderControllingContentLink = (): JSX.Element => {
    return (
      <TextLinkWidget
        className={ PermissionsLockUnlockStyles.TextLink }
        href={ this.state.controllingContent.url }
        kind={ TextLinkKind.Standard }
        target="_blank"
        testId={ CONTROLLING_CONTENT_LINK_TESTID }
      >
        { this.state.controllingContent.name }
      </TextLinkWidget>
    );
  }

  private renderBusyUnlocking = (): JSX.Element => {
    return (
      <span data-tb-test-id="unlock-in-progress">
        { Messages.permissionsUnlockInProgress() }
        <div className="tb-progress-bar">
          <span className="tb-bar">
            <div
              className="tb-progress"
              data-tb-test-id="permissions-progress-bar"
              style={{ width: `${this.state.percentComplete}%` }}
            ></div>
          </span>
          <span className="tb-percent-complete" data-tb-test-id="permissions-progress-number">{ this.state.percentComplete }</span>
        </div>
      </span>
    );
  }

  public render() {
    const lockState = this.getLockState();
    const nestedProjectsState = LockStatusHelper.getNestedProjectsState(this.props.authorizables);
    return (
      <div>
        <div className={ PermissionsLockUnlockStyles.ContentContainer }>
          { this.renderContent(lockState, nestedProjectsState) }
        </div>
        { this.state.showDialog &&
          <PermissionsLockDialog
            containerType={ this.props.authorizableType }
            containerMessages={ this.containerMessages }
            containersNumber={ this.props.authorizables.size() }
            containerName={ this.props.authorizables.first().name }
            onCancel={ this.handleCloseDialog }
            onSave={ this.handleUpdateLockStatus }
            initialLockState={ lockState }
            initialNestedProjectsState={ nestedProjectsState }
          />
        }
      </div>
    );
  }
}
